// components/PatientProfile.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface UserData {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  telefono: string;
  picture: string;
  auth_method: string;
  created_at: string;
  updated_at: string;
  dni?: string;
}

interface ProfileFormData {
  nombre: string;
  apellido: string;
  telefono: string;
  email: string;
  dni: string;
}

const PatientProfile: React.FC = () => {
  const navigate = useNavigate();
  
  const [userData, setUserData] = useState<UserData | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    nombre: '',
    apellido: '',
    telefono: '',
    dni: '',
    email: ''
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = () => {
    setIsLoading(true);
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        navigate('/login');
        return;
      }

      const user = JSON.parse(storedUser);
      setUserData(user);
      setFormData({
        nombre: user.nombre || '',
        apellido: user.apellido || '',
        telefono: user.telefono || '',
        dni: user.dni || '',
        email: user.email || ''
      });
    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
      setMessage({ type: 'error', text: 'Error cargando los datos del perfil' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Validaciones básicas
      if (!formData.nombre.trim() || !formData.apellido.trim()) {
        setMessage({ type: 'error', text: 'Nombre y apellido son obligatorios' });
        return;
      }

      if (!formData.email.trim()) {
        setMessage({ type: 'error', text: 'El email es obligatorio' });
        return;
      }

      // Apuntaba a http://localhost:5000, asi que en produccion el boton de
      // guardar no hacia nada: fallaba en silencio contra una direccion que
      // no existe fuera de la maquina del desarrollador.
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients/${userData?.id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Error al actualizar el perfil');
      }

      const result = await response.json();

      // Usar datos actualizados del backend
      const updatedUser = result.userForLocalStorage;

      // Actualizar localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUserData(updatedUser);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Perfil actualizado exitosamente' });

    } catch (error) {
      console.error('Error actualizando perfil:', error);
      setMessage({ type: 'error', text: 'Error al actualizar el perfil' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (userData) {
      setFormData({
        nombre: userData.nombre || '',
        apellido: userData.apellido || '',
        telefono: userData.telefono || '',
        dni: userData.dni || '',
        email: userData.email || ''
      });
    }
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // Implementar eliminación de cuenta en el backend
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/patients/${userData?.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ confirmation: 'CONFIRMAR ELIMINACION' })
      });

      // Limpiar localStorage y redirigir
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedIn');
      navigate('/');
    } catch (error) {
      console.error('Error eliminando cuenta:', error);
      setMessage({ type: 'error', text: 'Error al eliminar la cuenta' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error de sesión</h2>
          <p className="text-gray-600 mb-6">No se pudieron cargar los datos del usuario</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Mi Perfil</h1>
              <p className="text-gray-600 mt-1">Gestiona tu información personal y configuración</p>
            </div>
            <button
              onClick={() => navigate('/dashboard/paciente')}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Volver al Dashboard
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Información principal */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Información Personal</h2>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Editar
                  </button>
                ) : (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCancel}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {isSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                )}
              </div>

              {message.text && (
                <div className={`mb-6 p-4 rounded-lg ${
                  message.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {message.text}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre *
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{userData.nombre || 'No especificado'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido *
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.apellido}
                      onChange={(e) => handleInputChange('apellido', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{userData.apellido || 'No especificado'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{userData.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => handleInputChange('telefono', e.target.value)}
                      placeholder="Ej: +54 9 11 1234-5678"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{userData.telefono || 'No especificado'}</p>
                  )}
                </div>

                {/* El documento es opcional y sirve para una sola cosa: que
                    podamos mostrarle sus recetas, que se emiten a nombre del
                    DNI. Se explica ahí mismo para que no parezca un dato que
                    pedimos porque sí. Quien nunca va a tener recetas no tiene
                    por qué cargarlo. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DNI <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formData.dni}
                        onChange={(e) => handleInputChange('dni', e.target.value)}
                        placeholder="Sin puntos"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Sirve para mostrarte tus recetas electrónicas, que se emiten a nombre de tu documento.
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-900 py-2">{userData.dni || 'No especificado'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sección de seguridad */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mt-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Seguridad y Privacidad</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <h3 className="font-medium text-gray-900">Método de autenticación</h3>
                    <p className="text-sm text-gray-600">
                      {userData.auth_method === 'google' 
                        ? 'Iniciaste sesión con Google' 
                        : 'Iniciaste sesión con email y contraseña'
                      }
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${
                    userData.auth_method === 'google' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {userData.auth_method === 'google' ? 'Google' : 'Email'}
                  </div>
                </div>

                {userData.auth_method === 'manual' && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <h3 className="font-medium text-gray-900">Contraseña</h3>
                      <p className="text-sm text-gray-600">Cambia tu contraseña de acceso</p>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 font-medium">
                      Cambiar contraseña
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between py-3">
                  <div>
                    <h3 className="font-medium text-red-600">Eliminar cuenta</h3>
                    <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Eliminar cuenta
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Avatar y datos básicos */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-green-500">
                {userData.picture ? (
                  <img src={userData.picture} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl">
                    {userData.nombre?.charAt(0)}{userData.apellido?.charAt(0)}
                  </div>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {userData.nombre} {userData.apellido}
              </h3>
              <p className="text-gray-600">{userData.email}</p>
              <div className="mt-4 text-sm text-gray-500">
                Miembro desde {formatDate(userData.created_at)}
              </div>
            </div>

            {/* Estadísticas */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Citas totales</span>
                  <span className="font-medium">-</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Próxima cita</span>
                  <span className="font-medium">-</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Profesionales visitados</span>
                  <span className="font-medium">-</span>
                </div>
              </div>
            </div>

            {/* Acciones rápidas */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones rápidas</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/search')}
                  className="w-full text-left p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Agendar nueva cita
                </button>
                <button
                  onClick={() => navigate('/medical-history')}
                  className="w-full text-left p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Ver historial médico
                </button>
                <button
                  onClick={() => navigate('/dashboard/paciente')}
                  className="w-full text-left p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Mis citas
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de confirmación de eliminación */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-600 mb-4">Confirmar eliminación</h3>
              <p className="text-gray-700 mb-6">
                ¿Estás seguro de que quieres eliminar tu cuenta? Esta acción no se puede deshacer 
                y perderás todos tus datos, incluyendo citas e historial médico.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar cuenta
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientProfile;